import type { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { analytics } from '@silvertours/front-entities';
import Search from '@engineering/icons/search';
import { useSearchContext } from '../../SearchFormLegacy/context';
import {
  Form,
  Headline,
  Icon,
  AccessibleLabel,
  Label,
  Placeholder,
  SearchButton,
  VisuallyHidden,
  SubmitButtonWrapper,
} from './Form.styles';

type Props = {
  searchAction?: () => void;
  displayMode?: 'full' | 'thin';
  isModernStage?: boolean;
  /** `card` is a static card in the page flow (index page variant). */
  layout?: 'default' | 'card';
  /** Replaces the default headline. */
  headline?: string;
  /** Replaces the default label of the search button. */
  buttonLabel?: string;
};

export const SearchForm = ({
  searchAction,
  displayMode = 'full',
  isModernStage,
  layout = 'default',
  headline,
  buttonLabel,
}: Props) => {
  const t = useTranslations('features.stage');
  const { state } = useSearchContext();

  const handleSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    analytics.gtm.trackBeginSearchClicked();
    if (searchAction) {
      searchAction();
    }
  };

  const value = state?.inputValue.departure;
  const hasValue = !!value;
  const isThin = displayMode === 'thin';
  const isCard = layout === 'card';
  const label = buttonLabel ?? t('search.input.label');

  return (
    <Form
      id="search-wizard"
      role="search"
      as="form"
      $isThin={isThin}
      $isModernStage={isModernStage}
      $isCard={isCard}
    >
      {displayMode !== 'thin' && (
        <Headline>{headline ?? t('search.headline')}</Headline>
      )}
      <SearchButton
        type="button"
        aria-labelledby="search-label-text"
        aria-haspopup="true"
        onClick={handleSubmit}
        hasValue={hasValue}
        $isThin={isThin}
        $isCard={isCard}
      >
        {!isThin && !isCard && (
          <Icon aria-hidden="true">
            <Search width="24" height="24" />
          </Icon>
        )}
        {(isThin || isCard) && <Label>{t('search.input.floatingLabel')}</Label>}
        <Placeholder
          id={hasValue ? undefined : 'search-placeholder-text'}
          $isThin={isThin}
          $isCard={isCard}
        >
          {value || t('search.input.placeholder')}
        </Placeholder>
        <AccessibleLabel id="search-label-text">
          {isThin ? (
            <>
              <SubmitButtonWrapper>
                <Search width="24" height="24" aria-hidden="true" />
              </SubmitButtonWrapper>
              <VisuallyHidden>{label}</VisuallyHidden>
            </>
          ) : (
            <>
              {isCard && (
                <SubmitButtonWrapper>
                  <Search width="24" height="24" aria-hidden="true" />
                </SubmitButtonWrapper>
              )}
              {label}
            </>
          )}
        </AccessibleLabel>
      </SearchButton>
    </Form>
  );
};
